import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BACKEND_URL } from '../config/canonical';
import {
  FileSignature, AlertTriangle, CheckCircle, XCircle, Clock, User,
  Briefcase, Shield, Loader2, ArrowLeft,
} from 'lucide-react';

/**
 * Iter131c — Case Acceptance Sign screen (Master Directive Phases 11 + 12).
 *  - Drawn signatures removed globally; typed name is the legal signature.
 *  - The Sign & Accept Assignment button now unlocks as soon as every
 *    required acknowledgment is checked and the investigator has typed
 *    their full legal name.
 *  - Signatures write to the single `case_acceptance_signatures` record
 *    for this form, keeping the Acceptance Form and this sign screen on
 *    the exact same source of truth.
 */
const CaseAcceptanceSign = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  useAuth();

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [acknowledgments, setAcknowledgments] = useState({});
  const [typedName, setTypedName] = useState('');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const token = localStorage.getItem('nexus_token');

  const fetchForm = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/case-acceptance/forms/${formId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setForm(data);
        const acks = {};
        (data.required_acknowledgments || []).forEach((a) => { acks[a.id] = false; });
        setAcknowledgments(acks);
      } else if (res.status === 403) {
        setError('You do not have permission to view this form');
      } else if (res.status === 404) {
        setError('Form not found');
      }
    } catch (e) {
      setError('Failed to load form');
    } finally {
      setLoading(false);
    }
  }, [formId, token]);

  useEffect(() => { fetchForm(); }, [fetchForm]);

  const allAcknowledged = form?.required_acknowledgments?.length
    ? Object.values(acknowledgments).every((v) => v === true)
    : true;
  const canSubmit = allAcknowledged && typedName.trim().length >= 3;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/case-acceptance/forms/${formId}/sign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          typed_name: typedName.trim(),
          signature_data: '', // Iter131c — typed name is the legal signature
          acknowledgments,
          investigator_notes: notes || null,
        }),
      });
      if (res.ok) {
        alert('Case Acceptance Form signed successfully! You now have access to the case.');
        navigate('/case-acceptance');
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || 'Failed to submit form');
      }
    } catch (e) {
      alert('Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/case-acceptance/forms/${formId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejection_reason: rejectionReason }),
      });
      if (res.ok) {
        alert('Assignment rejected');
        navigate('/case-acceptance');
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || 'Failed to reject');
      }
    } catch (e) {
      alert('Failed to reject');
    } finally {
      setSubmitting(false);
      setShowRejectModal(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Card className="bg-red-900/20 border-red-500/30 max-w-md">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Error</h2>
            <p className="text-slate-300">{error}</p>
            <Button onClick={() => navigate('/case-acceptance')} className="mt-4">
              Back to Case Acceptance
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!form) return null;

  const canSign = ['PENDING_ACCEPTANCE', 'VIEWED'].includes(form.status);
  const isExpired = form.status === 'EXPIRED';
  const isSigned = form.status === 'SIGNED';
  const isRejected = form.status === 'REJECTED';

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6" data-testid="case-acceptance-sign-page">
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate('/case-acceptance')} className="mb-4 text-slate-400">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Case Acceptance
        </Button>

        <Card className="bg-slate-900 border-slate-700 mb-6">
          <CardHeader className="border-b border-slate-700 text-center pb-6">
            <div className="flex justify-center mb-4">
              <FileSignature className="w-16 h-16 text-amber-500" />
            </div>
            <CardTitle className="text-2xl text-white">Case Assignment Acceptance Agreement</CardTitle>
            <p className="text-slate-400 mt-2">Form ID: <span className="text-amber-400 font-mono">{form.id}</span></p>
            <Badge className={`mt-2 ${isSigned ? 'bg-emerald-500/20 text-emerald-400'
                : isRejected ? 'bg-red-500/20 text-red-400'
                : isExpired ? 'bg-slate-500/20 text-slate-400'
                : 'bg-amber-500/20 text-amber-400'}`}>
              {form.status.replace('_', ' ')}
            </Badge>
          </CardHeader>

          <CardContent className="p-6 space-y-8">
            {isExpired && (
              <div className="bg-slate-800/50 border border-slate-600 rounded-lg p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <h4 className="text-white font-semibold">Form Expired</h4>
                  <p className="text-slate-400 text-sm">Contact your administrator to resend the form.</p>
                </div>
              </div>
            )}
            {isSigned && (
              <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                <div>
                  <h4 className="text-emerald-400 font-semibold">Form Signed</h4>
                  <p className="text-slate-300 text-sm">Signed on {formatDate(form.signed_at)}</p>
                </div>
              </div>
            )}
            {isRejected && (
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                <div>
                  <h4 className="text-red-400 font-semibold">Assignment Rejected</h4>
                  <p className="text-slate-300 text-sm">Reason: {form.rejection_reason}</p>
                </div>
              </div>
            )}

            {/* Assignment Info */}
            <div>
              <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2 mb-4">
                <Briefcase className="w-5 h-5" /> Assignment Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-800/30 rounded-lg p-4">
                <div><span className="text-slate-400">Case Number:</span><span className="text-white ml-2 font-mono">{form.case_number || '-'}</span></div>
                <div><span className="text-slate-400">Case Type:</span><span className="text-white ml-2">{form.case_type || '-'}</span></div>
                <div><span className="text-slate-400">Client:</span><span className="text-white ml-2" data-testid="ca-sign-client">{form.client_name || form.client_type || '-'}</span></div>
                <div><span className="text-slate-400">Client Phone:</span><span className="text-white ml-2">{form.client_phone || '-'}</span></div>
                <div><span className="text-slate-400">Assigned Investigator:</span><span className="text-white ml-2">{form.investigator_full_name || '-'}</span></div>
                <div><span className="text-slate-400">Assignment Type:</span><span className="text-white ml-2">{form.assignment_type || '-'}</span></div>
                <div><span className="text-slate-400">Start Date:</span><span className="text-white ml-2">{formatDate(form.start_date)}</span></div>
                <div><span className="text-slate-400">End Date:</span><span className="text-white ml-2">{formatDate(form.end_date)}</span></div>
                <div><span className="text-slate-400">Due Date:</span><span className="text-white ml-2">{formatDate(form.case_due_date) || '-'}</span></div>
                {(form.case_description || form.case_summary) && (
                  <div className="col-span-full">
                    <span className="text-slate-400">Case Description:</span>
                    <p className="text-white text-sm mt-1">{form.case_description || form.case_summary}</p>
                  </div>
                )}
                <div><span className="text-slate-400">Location:</span><span className="text-white ml-2">{form.assignment_location || '-'}</span></div>
                <div><span className="text-slate-400">Scheduled Hours:</span><span className="text-white ml-2">{form.scheduled_hours || '-'}</span></div>
              </div>
              {form.special_instructions && (
                <div className="mt-4 bg-amber-900/20 border border-amber-500/30 rounded-lg p-4">
                  <h4 className="text-amber-400 font-semibold mb-2">Special Instructions</h4>
                  <p className="text-white whitespace-pre-wrap">{form.special_instructions}</p>
                </div>
              )}
            </div>

            {/* Investigator Info */}
            <div>
              <h3 className="text-lg font-semibold text-blue-400 flex items-center gap-2 mb-4">
                <User className="w-5 h-5" /> Investigator Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-800/30 rounded-lg p-4">
                <div><span className="text-slate-400">Full Name:</span><span className="text-white ml-2">{form.investigator_full_name}</span></div>
                <div><span className="text-slate-400">Username:</span><span className="text-white ml-2 font-mono">@{form.investigator_username}</span></div>
                <div><span className="text-slate-400">License #:</span><span className="text-white ml-2">{form.investigator_license_number || '-'}</span></div>
                <div><span className="text-slate-400">Phone:</span><span className="text-white ml-2">{form.investigator_phone || '-'}</span></div>
                <div><span className="text-slate-400">Email:</span><span className="text-white ml-2">{form.investigator_email || '-'}</span></div>
                <div><span className="text-slate-400">Role:</span><span className="text-white ml-2 capitalize">{form.investigator_role?.replace('_', ' ') || '-'}</span></div>
              </div>
            </div>

            {/* Agreement */}
            <div>
              <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5" /> Agreement Terms
              </h3>
              <div className="space-y-4">
                {(form.agreement_sections || []).map((section, idx) => (
                  <div key={section.id || idx} className="bg-slate-800/30 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-2">{section.title}</h4>
                    <p className="text-slate-300 whitespace-pre-wrap text-sm">{section.content}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Acknowledgments */}
            {canSign && (
              <div>
                <h3 className="text-lg font-semibold text-emerald-400 flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5" /> Required Acknowledgments
                </h3>
                <div className="space-y-3 bg-slate-800/30 rounded-lg p-4" data-testid="acknowledgment-list">
                  {(form.required_acknowledgments || []).map((ack) => (
                    <label key={ack.id} className="flex items-start gap-3 cursor-pointer group">
                      <Checkbox
                        checked={acknowledgments[ack.id] || false}
                        onCheckedChange={(checked) => setAcknowledgments((prev) => ({ ...prev, [ack.id]: !!checked }))}
                        className="mt-0.5 border-slate-500 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                        data-testid={`ack-${ack.id}`}
                      />
                      <span className="text-slate-300 group-hover:text-white transition-colors">{ack.text}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Signature (typed) */}
            {canSign && (
              <div>
                <h3 className="text-lg font-semibold text-amber-400 flex items-center gap-2 mb-4">
                  <FileSignature className="w-5 h-5" /> Electronic Signature
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-slate-300 text-sm block mb-2">Type Your Full Legal Name *</label>
                    <Input
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Enter your full legal name"
                      className="bg-slate-800 border-slate-600 text-white"
                      data-testid="typed-name-input"
                    />
                    {typedName && (
                      <div className="mt-3 p-3 bg-white rounded border border-slate-200">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Electronic Signature</p>
                        <p className="text-slate-900 text-2xl font-semibold" style={{ fontFamily: 'cursive' }} data-testid="typed-signature-preview">
                          {typedName}
                        </p>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-500 mt-2">
                      By typing your name above, you agree that this typed signature is the legal
                      equivalent of a handwritten signature under the E-SIGN Act.
                    </p>
                  </div>
                  <div>
                    <label className="text-slate-300 text-sm block mb-2">Notes (Optional)</label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any notes or comments..."
                      className="bg-slate-800 border-slate-600 text-white"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Submit / Reject */}
            {canSign && (
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-slate-700">
                {!canSubmit && (
                  <div className="w-full text-xs text-amber-300/80 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2" data-testid="submit-blocker-hint">
                    Check every acknowledgment above and type your full legal name to enable the Sign &amp; Accept button.
                  </div>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || submitting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                  data-testid="submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Sign &amp; Accept Assignment
                </Button>
                <Button
                  onClick={() => setShowRejectModal(true)}
                  disabled={submitting}
                  variant="outline"
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                  data-testid="reject-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Assignment
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-xs">
          Generated by NexusTrac Investigation Platform • Form ID: {form.id} • Generated: {formatDate(form.generated_at)}
        </p>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-slate-900 border-slate-700 max-w-md w-full">
            <CardHeader className="border-b border-slate-700">
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" /> Reject Assignment
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-slate-300 mb-4">
                Please provide a reason for rejecting this assignment. Your supervisor will be notified immediately.
              </p>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                className="bg-slate-800 border-slate-600 text-white"
                rows={4}
                data-testid="rejection-reason-input"
              />
              <div className="flex gap-3 mt-4">
                <Button onClick={() => setShowRejectModal(false)} variant="outline" className="flex-1 border-slate-600">
                  Cancel
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={!rejectionReason.trim() || submitting}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-reject-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Rejection'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CaseAcceptanceSign;
